import React from "react";
import { sanitiseString, concatStringFromClassesArrayList } from "../../Utils";
import { Button } from "../Button";
import { BoxContainer } from "../BoxContainer";

export interface Props {
  id?: string;
  summaries: Summaries[];
  classes?: string;
  dataTestId?: string;
  boxContainer?: React.ComponentProps<typeof BoxContainer>;
}

export interface Summaries {
  groups: SummaryGroup[];
  summaryTitle?: string;
}

export interface SummaryGroup {
  rows?: SummaryRow[];
  groupTitle?: string;
  id?: string;
  dataTestId?: string;
  noDividers?: boolean;
}

export interface SummaryRow {
  id?: string;
  rowItems: SummaryRowItem[];
  rowTitle?: string;
  error?: boolean;
  errorMessage?: string;
}

export interface SummaryRowItem {
  id?: string;
  dataTestId?: string;
  rowTitle?: string;
  rowTitleAttributes?: { [key: string]: string };
  valueList?: SummaryValue[];
  actions?: SummaryAction[];
  attributes?: { [key: string]: string };
}

export interface SummaryValue {
  text: string;
}

export interface SummaryAction {
  id?: string;
  dataTestId?: string;
  text: string;
  url: string;
  onClick: any;
  attributes?: { [key: string]: string };
  visuallyHiddenText?: string;
  isTertiaryButton?: boolean;
}

export const Summary = (props: Props) => {
  const summariesClassList = {
    defaultClass: "ons-summary",
    optionalClasses: [props.classes ? props.classes : null],
  };
  const summariesClassName =
    concatStringFromClassesArrayList(summariesClassList);

  const renderSummary = () => {
    return (
      <div
        className={summariesClassName}
        id={sanitiseString(props.id)}
        data-testid={sanitiseString(props.dataTestId)}
      >
        {props.summaries.map((summary, summaryIndex) => (
          <React.Fragment key={summaryIndex}>
            {summary.summaryTitle && (
              <h2
                className={`ons-summary__title ons-u-mb-m${
                  summaryIndex > 0 ? " ons-u-mt-m" : ""
                }`}
              >
                {summary.summaryTitle}
              </h2>
            )}
            {summary.groups.map((group, groupIndex) => (
              <div
                className="ons-summary__group"
                key={groupIndex}
                {...(group.id ? { id: group.id } : {})}
                data-testid={sanitiseString(group.dataTestId)}
              >
                {/* Render h3 or h2 based on the presence of summaryTitle */}
                {group.groupTitle && (
                  <>
                    {summary.summaryTitle ? (
                      <h3 className="ons-summary__group-title">
                        {group.groupTitle}
                      </h3>
                    ) : (
                      <h2 className="ons-summary__group-title">
                        {group.groupTitle}
                      </h2>
                    )}
                  </>
                )}
                {group.rows && (
                  <div className="ons-summary__items">
                    {group.noDividers ? (
                      <div>
                        {group.rows.map((row, rowIndex) => (
                          <div className="ons-u-mt-s" key={rowIndex}>
                            {row.rowItems.map((rowItem, rowItemIndex) => (
                              <dl
                                className="ons-u-mb-xs ons-grid ons-summary__row"
                                key={rowItemIndex}
                                {...(rowItem.id
                                  ? { id: sanitiseString(rowItem.id) }
                                  : {})}
                                data-testid={sanitiseString(rowItem.dataTestId)}
                              >
                                <dt className="ons-grid__col">
                                  <div>
                                    {rowItem.rowTitle
                                      ? rowItem.rowTitle
                                      : row.rowTitle}
                                  </div>
                                </dt>
                                {rowItem.actions && (
                                  <dd>
                                    {rowItem.actions.map(
                                      (action, actionIndex) =>
                                        action.isTertiaryButton ? (
                                          <Button
                                            id={sanitiseString(action.id)}
                                            dataTestId={sanitiseString(
                                              action.dataTestId
                                            )}
                                            key={actionIndex}
                                            variants="tertiary"
                                            text={action.text}
                                            onClick={action.onClick}
                                            assistiveTextPrefix={
                                              action.visuallyHiddenText
                                            }
                                          ></Button>
                                        ) : (
                                          <React.Fragment key={actionIndex}>
                                            <a
                                              key={actionIndex}
                                              href={action.url}
                                              onClick={action.onClick}
                                              className="ons-summary__button"
                                            >
                                              <span
                                                className="ons-summary__button-text"
                                                aria-hidden="true"
                                              >
                                                {action.text}
                                              </span>
                                              <span className="ons-u-vh">
                                                {action.visuallyHiddenText}
                                              </span>
                                            </a>
                                          </React.Fragment>
                                        )
                                    )}
                                  </dd>
                                )}
                              </dl>
                            ))}
                          </div>
                        ))}
                      </div>
                    ) : (
                      group.rows.map((row, rowIndex) => (
                        <div
                          className={`ons-summary__item${
                            row.error ? " ons-summary__item--error" : ""
                          }`}
                          key={rowIndex}
                          {...(row.id ? { id: row.id } : {})}
                        >
                          {row.errorMessage && (
                            <div className="ons-summary__row-title--error ons-u-fs-r">
                              {row.errorMessage}
                            </div>
                          )}

                          {row.rowItems.length > 1 && row.rowTitle && (
                            <div className="ons-summary__row-title ons-summary__row-title--no-group-title ons-u-fs-r">
                              {row.rowTitle}
                            </div>
                          )}

                          {row.rowItems.map((rowItem, rowItemIndex) => (
                            <dl
                              className={`ons-summary__row${
                                rowItem.valueList
                                  ? " ons-summary__row--has-values"
                                  : ""
                              }`}
                              key={rowItemIndex}
                              {...(rowItem.id ? { id: rowItem.id } : {})}
                            >
                              <dt className="ons-summary__item-title">
                                <div className="ons-summary__item--text">
                                  {rowItem.rowTitle
                                    ? rowItem.rowTitle
                                    : row.rowTitle}
                                </div>
                              </dt>

                              {rowItem.valueList && (
                                <dd
                                  className={`ons-summary__values${
                                    !rowItem.actions
                                      ? " ons-summary__values--2"
                                      : ""
                                  }`}
                                  {...(rowItem.attributes
                                    ? Object.entries(rowItem.attributes)
                                        .map(([attribute, value]) => ({
                                          [attribute]: value,
                                        }))
                                        .reduce(
                                          (acc, attribute) => ({
                                            ...acc,
                                            ...attribute,
                                          }),
                                          {}
                                        )
                                    : {})}
                                >
                                  {rowItem.valueList.length === 1 ? (
                                    <span className="ons-summary__text">
                                      {rowItem.valueList[0].text}
                                    </span>
                                  ) : (
                                    <ul className="ons-u-mb-no">
                                      {rowItem.valueList.map(
                                        (value, valueIndex) => (
                                          <li key={valueIndex}>
                                            <span className="ons-summary__text">
                                              {value.text}
                                            </span>
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  )}
                                </dd>
                              )}
                              {rowItem.actions && (
                                <dd className="ons-summary__actions">
                                  {rowItem.actions.map((action, actionIndex) =>
                                    action.isTertiaryButton ? (
                                      <Button
                                        id={sanitiseString(action.id)}
                                        dataTestId={sanitiseString(
                                          action.dataTestId
                                        )}
                                        key={actionIndex}
                                        variants="tertiary"
                                        text={action.text}
                                        onClick={action.onClick}
                                        classes="ons-summary__button"
                                        assistiveTextPrefix={
                                          action.visuallyHiddenText
                                        }
                                      ></Button>
                                    ) : (
                                      <React.Fragment key={actionIndex}>
                                        {actionIndex > 0 && (
                                          <span className="ons-summary__spacer"></span>
                                        )}

                                        <a
                                          id={sanitiseString(action.id)}
                                          data-testid={sanitiseString(
                                            action.dataTestId
                                          )}
                                          key={actionIndex}
                                          href={action.url}
                                          onClick={action.onClick}
                                          className="ons-summary__button"
                                          {...(action.attributes
                                            ? Object.entries(action.attributes)
                                                .map(([attribute, value]) => ({
                                                  [attribute]: value,
                                                }))
                                                .reduce(
                                                  (acc, attribute) => ({
                                                    ...acc,
                                                    ...attribute,
                                                  }),
                                                  {}
                                                )
                                            : {})}
                                        >
                                          <span
                                            className="ons-summary__button-text"
                                            aria-hidden="true"
                                          >
                                            {action.text}
                                          </span>
                                          <span className="ons-u-vh">
                                            {action.visuallyHiddenText}
                                          </span>
                                        </a>
                                      </React.Fragment>
                                    )
                                  )}
                                </dd>
                              )}
                            </dl>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return props.boxContainer ? (
    <BoxContainer {...props.boxContainer}>{renderSummary()}</BoxContainer>
  ) : (
    renderSummary()
  );
};
